//
// Stimulus Controller: volunteering--program-activity-settings
//
// This controller manages the state of a related group of checkboxes and inputs used to
// configure the activity submission and reminder workflow. It dynamically enables, disables,
// and syncs the values of inputs based on the state of parent checkboxes.
//
// === Behavior ===
//
// - If the "early activity submission" checkbox is checked:
//     - The main "activity submission" checkbox is force-checked and disabled.
// - If "activity submission" is checked:
//     - The "activity reminder" checkbox is enabled.
//     - The "reminder offset" input becomes editable.
// - If "activity reminder" is not checked:
//     - The "activity reminder" checkbox is unchecked and disabled.
//     - The "reminder offset" input is cleared and made read-only.
// - If "activity reminder" is checked:
//     - The "follow-up reminder" checkbox is enabled.
//     - The "follow-up interval" input becomes editable.
// - If "follow-up reminder" is not checked:
//     - The "follow-up reminder" checkbox is unchecked and disabled.
//     - The "follow-up interval" input is cleared and made read-only.
//
// === Notes ===
//
// - When a checkbox is disabled but needs to preserve its value (e.g., force-checked),
//   a hidden input is inserted with the same name and value to ensure the value is submitted.
// - Visual state updates (e.g., Material Design Lite classes like .is-disabled, .is-dirty)
//   are applied or removed as appropriate when toggling inputs.
// - Change events are dispatched manually for any checkbox whose state changes to support MDL
//

import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "activitySubmissionCheckbox",
    "earlyActivitySubmissionCheckbox",
    "activityReminderCheckbox",
    "reminderOffsetInput",
    "followUpReminderCheckbox",
    "followUpIntervalInput"
  ]

  connect() {
    this.updateActivitySubmissionCheckbox()
  }

  updateActivitySubmissionCheckbox() {
    if(this.earlyActivitySubmissionCheckboxTarget.checked) {
      this.setAndDisableCheckbox(this.activitySubmissionCheckboxTarget, true)
    } else {
      this.enableCheckbox(this.activitySubmissionCheckboxTarget)
    }
    this.updateReminderCheckbox()
  }

  updateReminderCheckbox() {
    if(this.activitySubmissionCheckboxTarget.checked) {
      this.enableCheckbox(this.activityReminderCheckboxTarget)
    } else {
      this.setAndDisableCheckbox(this.activityReminderCheckboxTarget, false)
    }
    this.updateReminderOffsetInput()
    this.updateFollowupReminderCheckbox()
  }

  updateReminderOffsetInput() {
    if(this.activityReminderCheckboxTarget.checked) {
      this.enableInput(this.reminderOffsetInputTarget)
    } else {
      this.clearAndDisableInput(this.reminderOffsetInputTarget)
    }
  }

  updateFollowupReminderCheckbox() {
    if(this.activityReminderCheckboxTarget.checked) {
      this.enableCheckbox(this.followUpReminderCheckboxTarget)
    } else {
      this.setAndDisableCheckbox(this.followUpReminderCheckboxTarget, false)
    }
    this.updatefollowupIntervalInput()
  }

  updatefollowupIntervalInput() {
    if(this.followUpReminderCheckboxTarget.checked) {
      this.enableInput(this.followUpIntervalInputTarget)
    } else {
      this.clearAndDisableInput(this.followUpIntervalInputTarget)
    }
  }

  setAndDisableCheckbox(checkbox, isChecked) {
    checkbox.checked = isChecked
    checkbox.disabled = true
    this.insertMirrorInput(checkbox)
    this.dispatchVisualChange(checkbox)
  }

  enableCheckbox(checkbox) {
    checkbox.disabled = false
    this.removeMirrorInput(checkbox)
    this.dispatchVisualChange(checkbox)
  }

  clearAndDisableInput(input) {
    input.value = null
    input.readOnly = true
    input.parentNode.classList.add('is-disabled')
    input.parentNode.classList.remove('is-dirty')
  }

  enableInput(input) {
    input.readOnly = false
    input.parentNode.classList.remove('is-disabled')
  }

  insertMirrorInput(checkbox) {
    // Only add a hidden input if one doesn't already exist
    const next = checkbox.nextElementSibling
    if (next?.dataset?.mirror === "true") return

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = checkbox.name
    hidden.value = checkbox.value
    hidden.dataset.mirror = "true"

    checkbox.after(hidden)
  }

  removeMirrorInput(checkbox) {
    const next = checkbox.nextElementSibling
    if (next?.dataset?.mirror === "true") {
      next.remove()
    }
  }

  dispatchVisualChange(checkbox) {
    const evt = new Event("change", { bubbles: true })
    checkbox.dispatchEvent(evt)
  }
}
